// The light dots of the six game tabs (R3262, ui-map-interface.md §16.4):
// a dot on a tab says that something under it waits for the commander.
// The dot is the light dot of the thumb dock (`lightDot`), a shape and not
// only an ink, and the tab says its word to a screen reader through its
// label ("CAPTAIN, WAITS"), as the hub's section dot does (R2153).
//
// What waits under each tab:
//
// - CAPTAIN: a cadet waits for a place in the roster and the roster has a
//   free slot, or a captain whose injury ended is fit again. "Fit again" is a change between two empire
//   frames, so `TabDots` remembers it until the commander opens CAPTAIN.
// - FLEET: a damaged hull (wear above 0) waits in the pens, a fleet at sea
//   that is not anchored has no mission, or a fleet withdraws (the
//   `withdrawing` reader of `TabWorld`) (R3306).
// - BUILD: the build queue is empty.
// - WAR: the window of an object that concerns the reader stands open.
//
// DRAFT and ECONOMY carry no dot. A dot never shows on the open tab: to
// open the tab is to see what waits. The open menu says why in one line,
// the banner (`tabWhyHtml`, R3312): it names the first thing that waits
// under the open tab. The TAB DOTS switch of INTERFACE turns every dot and
// the banner off (R3306).

import type { Captain } from '../gen/proto/aquaphobia';
import { Mission } from '../gen/proto/aquaphobia';
import { overlay as S } from '../gen/strings';
import { lightDot } from './overlay/thumbdock';
import { esc } from './esc';
import { tabLabel } from './overlay/tables';
import type { GameTab } from './tabglyphs';

/**
 * Writes the light dot of one tab button and the word its label says to
 * a screen reader. A tab whose dot did not change is not touched. The tab
 * keeps the state in `data-dot`, so the form of `fitTabs`, which writes
 * the inside of the tab again, draws the dot back.
 */
export function writeTabDot(b: HTMLElement, on: boolean): void {
  const want = on ? '1' : '';
  if ((b.dataset.dot ?? '') === want) return;
  b.dataset.dot = want;
  b.querySelector(':scope > .ldot')?.remove();
  if (on) b.insertAdjacentHTML('beforeend', lightDot(true));
  const name = tabLabel(b.dataset.tab ?? '');
  b.setAttribute('aria-label', on ? S.tab_waits_said({ name }) : name);
}

/** What the dots read, off the empire frame, the fleet layer and the
 *  siege windows. */
export interface TabWorld {
  /** The empire frame has arrived: no dot stands before it. */
  empire: boolean;
  captains: readonly Captain[];
  /** The captain slots of the roster: a cadet waits only while one is free. */
  captainSlots: number;
  /** The hulls in the pens: a fleet id each, its own damage in percent and
   *  whether a repair holds it. */
  penned: readonly { fleetId: number; wearPct: number; underRepair: boolean }[];
  /** The own fleets at sea, and whether an anchor holds each one. */
  fleets: readonly { id: number; mission: Mission; anchored: boolean }[];
  /** Does the fleet withdraw? The overlay reads `FleetState.withdrawing`
   *  (R3261); with no reader, no fleet withdraws. */
  withdrawing?: (fleetId: number) => boolean;
  /** The jobs in the build queue. */
  queueLength: number;
  /** The window of an object that concerns the reader stands open. */
  windowOpen: boolean;
}

/** The dot of each game tab. */
export type TabWaits = Record<GameTab, boolean>;

/** Why a tab waits: one key for each reason that lights a dot. */
export type TabWhy =
  | 'captain_fit' | 'captain_cadet'
  | 'fleet_wear' | 'fleet_idle' | 'fleet_withdraw'
  | 'build_queue' | 'war_window';

/** The banner line of each reason, in the picked language. */
const WHY_WORDS: Record<TabWhy, () => string> = {
  captain_fit: () => S.tab_why_captain_fit,
  captain_cadet: () => S.tab_why_captain_cadet,
  fleet_wear: () => S.tab_why_fleet_wear,
  fleet_idle: () => S.tab_why_fleet_idle,
  fleet_withdraw: () => S.tab_why_fleet_withdraw,
  build_queue: () => S.tab_why_build_queue,
  war_window: () => S.tab_why_war_window,
};

/**
 * The banner over the open menu (R3312): one short line that says why the
 * dot of this tab stands (or stood, before the tab opened). '' while
 * nothing waits under the tab.
 */
export function tabWhyHtml(why: TabWhy | null): string {
  if (!why) return '';
  return `<div class="tabwhy" role="status">${lightDot(true)}<span>${esc(WHY_WORDS[why]())}</span></div>`;
}

/** The dots, with the memory of the captains that are fit again. */
export class TabDots {
  /** The captains whose injury the last frame stated. */
  private hurt = new Set<number>();
  /** The captains that are fit again since the commander last opened
   *  CAPTAIN. */
  private fit = new Set<number>();
  /** The first frame only learns who is hurt: a captain that was fit at
   *  the join is not fit AGAIN. */
  private primed = false;

  /** Reads the captains of one empire frame. A captain that was hurt in
   *  the last frame and is fit, at liberty and on the roster in this one
   *  is fit again. */
  read(captains: readonly Captain[]): void {
    const hurt = new Set<number>();
    for (const c of captains) {
      if (c.cadet || c.capturedBy) continue;
      if (c.injuredSecs > 0) hurt.add(c.id);
      else if (this.primed && this.hurt.has(c.id)) this.fit.add(c.id);
    }
    for (const id of this.fit) {
      if (!captains.some((c) => c.id === id)) this.fit.delete(id);
    }
    for (const id of this.shown) {
      if (!captains.some((c) => c.id === id)) this.shown.delete(id);
    }
    this.hurt = hurt;
    this.primed = true;
  }

  /** The captains the open CAPTAIN menu took the news of: the banner
   *  keeps saying it while the menu stands open, and it clears when the
   *  commander leaves CAPTAIN. */
  private shown = new Set<number>();

  /**
   * The first reason that lights each tab, null where nothing waits. An
   * open CAPTAIN takes the news of the captains that are fit again, and
   * keeps it for the banner until it closes. The reason of the open tab
   * stands in the result: the dot never shows there, the banner does.
   */
  private reasons(w: TabWorld, open: string): Record<GameTab, TabWhy | null> {
    const out: Record<GameTab, TabWhy | null> = { BUILD: null, DRAFT: null, FLEET: null, CAPTAIN: null, ECONOMY: null, WAR: null };
    if (open === 'CAPTAIN') {
      for (const id of this.fit) this.shown.add(id);
      this.fit.clear();
    } else {
      this.shown.clear();
    }
    if (!w.empire) return out;
    const withdrawing = w.withdrawing ?? (() => false);
    const seated = w.captains.filter((c) => !c.cadet).length;
    if (this.fit.size > 0 || this.shown.size > 0) out.CAPTAIN = 'captain_fit';
    else if (seated < w.captainSlots && w.captains.some((c) => c.cadet)) out.CAPTAIN = 'captain_cadet';
    if (w.penned.some((p) => p.wearPct > 0 && !p.underRepair)) out.FLEET = 'fleet_wear';
    else if (w.fleets.some((f) => f.mission === Mission.MISSION_UNSPECIFIED && !f.anchored)) out.FLEET = 'fleet_idle';
    else if (w.fleets.some((f) => withdrawing(f.id))) out.FLEET = 'fleet_withdraw';
    if (w.queueLength === 0) out.BUILD = 'build_queue';
    if (w.windowOpen) out.WAR = 'war_window';
    return out;
  }

  /** The dot of each tab, with no dot on the open tab `open` ('' while no
   *  menu stands open). An open CAPTAIN takes the news of the captains
   *  that are fit again. */
  waits(w: TabWorld, open: string): TabWaits {
    const why = this.reasons(w, open);
    const out: TabWaits = { BUILD: false, DRAFT: false, FLEET: false, CAPTAIN: false, ECONOMY: false, WAR: false };
    for (const t of Object.keys(out) as GameTab[]) out[t] = why[t] !== null && t !== open;
    return out;
  }

  /** The reason for the banner of the open tab `open`, or null (R3312). */
  why(w: TabWorld, open: string): TabWhy | null {
    const why = this.reasons(w, open);
    return open in why ? why[open as GameTab] : null;
  }
}
